import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Grid,
  Divider
} from '@mui/material';
import { addNode, createNodeRequest, NodeType } from '../api/services/computeService/NodeService';
import { useKeycloak } from '@react-keycloak/web';

const AddNodeForm = ({ open, onClose, onSuccess }) => {
  const { keycloak } = useKeycloak();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    ipAddress: '',
    port: '22',
    region: 'us-east-1',
    nodeType: NodeType.NODE_TYPE_COMPUTE,
    username: 'username',
    password: 'password',
    privateKey: ''
  });

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!formData.ipAddress || !formData.username) {
      setError('IP Address and Username are required');
      return;
    }

    if (!formData.password && !formData.privateKey) {
      setError('Please provide either a password or an SSH private key for authentication');
      return;
    }

    if (!keycloak.token) {
      setError('Authentication required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await keycloak.updateToken(30);
      
      const nodeRequest = createNodeRequest(formData);
      const response = await addNode({
        ...nodeRequest,
        token: keycloak.token
      });

      if (response.success) {
        onSuccess && onSuccess(response.data);
        handleClose();
      } else {
        setError(response.error || 'Failed to add node');
      }
    } catch (error) {
      console.error('Add node error:', error);
      setError('Failed to add node: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      ipAddress: '',
      port: '22',
      region: 'us-east-1',
      nodeType: NodeType.NODE_TYPE_COMPUTE,
      username: 'username',
      password: 'password',
      privateKey: ''
    });
    setError('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Add New Node</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Address Details
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="IP Address"
                value={formData.ipAddress}
                onChange={handleChange('ipAddress')}
                required
                placeholder="80.239.149.57"
                helperText="The IP address of the node"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Port"
                type="number"
                value={formData.port}
                onChange={handleChange('port')}
                required
                helperText="SSH port (default: 22)"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Region</InputLabel>
                <Select
                  value={formData.region}
                  onChange={handleChange('region')}
                  label="Region"
                >
                  <MenuItem value="us-east-1">US East 1</MenuItem>
                  <MenuItem value="us-west-1">US West 1</MenuItem>
                  <MenuItem value="us-west-2">US West 2</MenuItem>
                  <MenuItem value="eu-west-1">EU West 1</MenuItem>
                  <MenuItem value="eu-west-2">EU West 2</MenuItem>
                  <MenuItem value="eu-central-1">EU Central 1</MenuItem>
                  <MenuItem value="ap-south-1">Asia Pacific South 1</MenuItem>
                  <MenuItem value="ap-southeast-1">Asia Pacific Southeast 1</MenuItem>
                  <MenuItem value="ap-northeast-1">Asia Pacific Northeast 1</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Node Type</InputLabel>
                <Select
                  value={formData.nodeType}
                  onChange={handleChange('nodeType')}
                  label="Node Type"
                >
                  <MenuItem value={NodeType.NODE_TYPE_COMPUTE}>Compute</MenuItem>
                  <MenuItem value={NodeType.NODE_TYPE_STORAGE}>Storage</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                SSH Credentials
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Provide at least one authentication method (password or SSH key). You can provide both for flexibility.
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={handleChange('username')}
                required
                helperText="SSH username for the node"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={handleChange('password')}
                helperText="SSH password (optional if SSH key is provided)"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SSH Private Key (Optional)"
                multiline
                rows={6}
                value={formData.privateKey}
                onChange={handleChange('privateKey')}
                placeholder="-----BEGIN OPENSSH PRIVATE KEY-----
MIIEowIBAAKCAQEA...
-----END OPENSSH PRIVATE KEY-----"
                helperText="SSH private key (optional if password is provided) - supports RSA, ECDSA, and Ed25519 keys"
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Authentication Options:</strong>
                  <br />
                  • You can provide either a password or SSH key (or both)
                  <br />
                  • If using SSH key, ensure the corresponding public key is in the server's authorized_keys
                  <br />
                  • SSH key authentication is generally more secure than password authentication
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Adding Node...' : 'Add Node'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddNodeForm; 